import './App.css';
import { useAuth0 } from '@auth0/auth0-react';
import { Routes, Route, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Home from './Home';
import Profile from './Profile';
import Account from './Account';
import Plans from './Plans';
import Opportunities from './Opportunities';
import OpportunityDetail from './OpportunityDetail';
import Blog from './Blog';
import BlogPost from './BlogPost';
import Forum from './Forum';
import Classifieds from './Classifieds';
import ThreadDetail from './ThreadDetail';
import MemberProfile from './MemberProfile';
import Leaderboard from './Leaderboard';
import PlugYourWork from './PlugYourWork';
import PlugYourWorkSubmit from './PlugYourWorkSubmit';
import EditorDatabase from './EditorDatabase';
import Alerts from './Alerts';
import TermsOfService from './TermsOfService';
import Podcast from './Podcast';
import SignUp from './SignUp';
import Welcome from './Welcome';
import NavBar from './NavBar';
import Footer from './Footer';
import BackgroundAccents from './BackgroundAccents';
import ScrollToTop from './components/ScrollToTop';
import MemberActivityReporter from './components/MemberActivityReporter';
import React, { useEffect, useState, useRef } from 'react';
import { useAuthenticatedApi } from './api';
import { useSubscriptionCheck } from './hooks/useSubscriptionCheck';
import { CSRFProvider } from './contexts/CSRFContext';
import { fetchWithCSRF } from './utils/csrf';
import { captureFirstTouchFromUrl, getFirstTouchQueryString } from './utils/attribution';
import { Toaster, toast } from 'react-hot-toast';

// Create a client with conservative settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes - shorter stale time
      cacheTime: 5 * 60 * 1000, // 5 minutes - shorter cache time
      retry: 1,
      retryDelay: 2000, // 2 second delay between retries
      refetchOnWindowFocus: false,
      refetchOnMount: true, // Always refetch on mount to ensure fresh data
      refetchOnReconnect: true, // Refetch when reconnecting
    },
  },
});

function App() {
  const { isAuthenticated, isLoading: authLoading, user, loginWithPopup, error } = useAuth0();
  const { authenticatedFetch } = useAuthenticatedApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const checkoutProcessedRef = useRef(new Set()); // Track processed session IDs
  const upgradeProcessedRef = useRef(false); // Track if upgrade/downgrade toast has been shown
  const justProcessedCheckoutRef = useRef(false); // Flag to prevent subscription check after checkout

  useEffect(() => {
    captureFirstTouchFromUrl();
  }, []);
  
  // Simple subscription check - only for authenticated users
  const { hasSubscription, isLoading: subscriptionLoading, setSubscriptionStatus, refetch: refetchSubscription, error: subscriptionError } = useSubscriptionCheck(justProcessedCheckoutRef);

  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
  };

  const handleSignup = async () => {
    if (!email) {
      showMessage('Please enter your email address first', 'error');
      return;
    }

    setIsSavingEmail(true);
    try {
      // Save email to database with status "new" (no auth required)
      const ensureBody = { 
          email: email,
          name: null // We don't have the name yet
        };
      const firstTouchQs = getFirstTouchQueryString();
      if (firstTouchQs) ensureBody.query_string = firstTouchQs;
      const response = await fetchWithCSRF(`${process.env.REACT_APP_API_URL || '/api'}/members/ensure`, {
        method: 'POST',
        body: JSON.stringify(ensureBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 409) {
          // Email already exists - redirect to login instead
          showMessage(`You already have an account with ${email}. Please use the "Log In" button instead.`, 'error');
          return;
        } else {
          console.error('Database save failed:', errorData);
          // Continue to Auth0 signup even if database save fails
        }
      }

      // Open Auth0 signup with pre-filled email (for new users)
      await loginWithPopup({ 
        authorizationParams: { 
          screen_hint: 'signup',
          login_hint: email
        } 
      });
    } catch (error) {
      // Check if it's a popup closed error
      if (error.error === 'cancelled' || error.message === 'Popup closed') {
        // Show helpful message with forgot password link
        showMessage('Sign up was cancelled. Please try again.', 'error');
        return;
      }
      
      console.error('Error saving email or opening signup:', error);
      
      // Still try to open Auth0 signup even if database save fails
      try {
        await loginWithPopup({ 
          authorizationParams: { 
            screen_hint: 'signup',
            login_hint: email
          } 
        });
      } catch (authError) {
        // Check if this is also a popup closed error
        if (authError.error === 'cancelled' || authError.message === 'Popup closed') {
          showMessage('Sign up was cancelled. Please try again.', 'error');
          return;
        }
        
        console.error('Auth0 signup failed:', authError);
      }
    } finally {
      setIsSavingEmail(false);
    }
  };

  // Redirect to /plans if authenticated but no subscription (except on /plans page)
  // Don't redirect if we're processing a checkout success (user just paid)
  useEffect(() => {
    const isProcessingCheckout = searchParams.get('success') === 'true' && searchParams.get('session_id');
    // Don't redirect if we just processed checkout (checkoutProcessedRef has the sessionId)
    const hasProcessedCheckout = searchParams.get('session_id') && checkoutProcessedRef.current.has(searchParams.get('session_id'));
    // Don't redirect if we just processed checkout (justProcessedCheckoutRef flag is set)
    const justProcessedCheckout = justProcessedCheckoutRef.current;
    
    if (isAuthenticated && !subscriptionLoading && hasSubscription === false && location.pathname !== '/plans' && !isProcessingCheckout && !hasProcessedCheckout && !justProcessedCheckout) {
      navigate(`/plans${location.search || ''}`);
    }
  }, [isAuthenticated, hasSubscription, subscriptionLoading, location.pathname, navigate, searchParams]);

  // Handle successful checkout return from Stripe
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    const upgraded = searchParams.get('upgraded');
    
    // Handle upgrade/downgrade success (subscription modified directly, no checkout session)
    if (upgraded === 'true' && !upgradeProcessedRef.current) {
      upgradeProcessedRef.current = true;
      const isDowngrade = searchParams.get('downgrade') === 'true';
      // Set flag to prevent subscription check from running
      justProcessedCheckoutRef.current = true;
      // Manually set subscription status FIRST to prevent redirect and double loading
      setSubscriptionStatus('subscribed');
      // Clear URL parameters
      setTimeout(() => {
        window.history.replaceState({}, document.title, '/');
        // Clear flag after a short delay to allow state to settle
        setTimeout(() => {
          justProcessedCheckoutRef.current = false;
          upgradeProcessedRef.current = false; // Reset after processing
        }, 1000);
      }, 0);
      // Show success message based on upgrade or downgrade (only once)
      if (isDowngrade) {
        toast.success('Subscription downgraded successfully! Credit has been applied to your account.');
      } else {
        toast.success('Subscription upgraded successfully! You were charged only the prorated difference.');
      }
      return;
    }
    
    // Prevent processing the same checkout success multiple times
    if (success === 'true' && sessionId && !checkoutProcessedRef.current.has(sessionId)) {
      checkoutProcessedRef.current.add(sessionId);
      
      const handleCheckoutSuccess = async () => {
        try {
          const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/checkout-success?session_id=${sessionId}`);
          
          if (response.ok) {
            const responseData = await response.json();
            // Set flag to prevent subscription check from running
            justProcessedCheckoutRef.current = true;
            // Manually set subscription status FIRST to prevent redirect and double loading
            setSubscriptionStatus('subscribed');
            // Send to welcome page (how did you hear about us)
            setTimeout(() => {
              navigate('/welcome', { replace: true });
              const delay = process.env.NODE_ENV === 'production' ? 3000 : 1500;
              setTimeout(() => {
                justProcessedCheckoutRef.current = false;
                refetchSubscription();
              }, delay);
            }, 0);
            // Show success message
            toast.success('Subscription activated successfully!');
          } else {
            const errorData = await response.json();
            console.error('Failed to activate subscription:', errorData);
            const errorMessage = errorData.message || errorData.error || 'Failed to activate subscription. Please contact support.';
            toast.error(errorMessage);
            checkoutProcessedRef.current.delete(sessionId); // Allow retry on error
          }
        } catch (err) {
          console.error('❌ Error processing checkout success:', err);
          toast.error('Error processing payment. Please contact support.');
          checkoutProcessedRef.current.delete(sessionId); // Allow retry on error
        }
      };

      handleCheckoutSuccess();
    }
  }, [isAuthenticated, searchParams, authenticatedFetch, setSubscriptionStatus, refetchSubscription, navigate]);

  // Determine if we should show loading screen
  // Public pages: opportunity detail pages (for sharing), /plans (for signup), and /sign-up (marketing landing page)
  const isPublicPage = (location.pathname.startsWith('/opportunity/') && location.pathname !== '/opportunities') || location.pathname === '/plans' || location.pathname === '/sign-up';
  
  // Check if we should redirect to /plans (before rendering to prevent Home from loading)
  const isProcessingCheckout = searchParams.get('success') === 'true' && searchParams.get('session_id');
  const hasProcessedCheckout = searchParams.get('session_id') && checkoutProcessedRef.current.has(searchParams.get('session_id'));
  const justProcessedCheckout = justProcessedCheckoutRef.current;
  const shouldRedirectToPlans = isAuthenticated && !subscriptionLoading && hasSubscription === false && location.pathname !== '/plans' && location.pathname !== '/welcome' && !isProcessingCheckout && !hasProcessedCheckout && !justProcessedCheckout;
  
  // Show loading while:
  // 1. Auth0 is loading, OR
  // 2. User is authenticated and we're checking subscription (unless on public page), OR
  // 3. We're about to redirect to /plans (to prevent Home from loading)
  const shouldShowLoading = authLoading || (isAuthenticated && subscriptionLoading && !isPublicPage) || shouldRedirectToPlans;
  
  if (shouldShowLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Subscription check failed after retries (e.g. Auth0 slow/unresponsive)
  if (isAuthenticated && !isPublicPage && subscriptionError && hasSubscription === null) {
    return (
      <div style={{ 
        padding: '4rem 2rem',
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <h2 style={{ color: '#e74c3c', marginBottom: '1rem' }}>Unable to Verify Subscription</h2>
        <p style={{ color: '#666', marginBottom: '2rem' }}>{subscriptionError.message}</p>
        <button 
          onClick={() => refetchSubscription()}
          style={{
            backgroundColor: '#38b44a',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '4rem 2rem',
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <h1 style={{ color: '#e74c3c', marginBottom: '1rem' }}>Auth Error</h1>
        <p style={{ color: '#666', marginBottom: '2rem' }}>{error.message}</p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            backgroundColor: '#38b44a',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  // Require authentication for all pages except public pages
  if (!isAuthenticated && !isPublicPage) {
    return (
      <div style={{ 
        padding: '4rem 2rem',
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <h1 style={{ 
          fontSize: '3rem',
          fontWeight: '700',
          color: '#333',
          marginBottom: '1rem'
        }}>
          Welcome to Study Hall
        </h1>
        <p style={{ 
          fontSize: '1.25rem',
          color: '#666',
          marginBottom: '2rem',
          lineHeight: '1.6'
        }}>
          Join our community of media professionals and discover new opportunities, 
          share your work, and connect with like-minded individuals.
        </p>
        
        {/* Message Display */}
        {message && (
          <div style={{
            padding: '1rem',
            marginBottom: '2rem',
            borderRadius: '6px',
            backgroundColor: messageType === 'error' ? '#fee' : '#efe',
            color: messageType === 'error' ? '#c33' : '#363',
            border: `1px solid ${messageType === 'error' ? '#fcc' : '#cfc'}`,
            fontSize: '0.9rem'
          }}>
            {message}
          </div>
        )}
        
        <div style={{ marginBottom: '2rem' }}>
          <input
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '300px',
              padding: '12px 16px',
              border: '2px solid #e9ecef',
              borderRadius: '6px',
              fontSize: '16px',
              marginBottom: '1rem'
            }}
          />
        </div>
        
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button 
            onClick={async () => {
              try {
                const trimmedEmail = email?.trim();
                const returnTo = searchParams.get('returnTo') || window.location.pathname + window.location.search;
                await loginWithPopup({ 
                  appState: { returnTo },
                  authorizationParams: trimmedEmail ? { 
                    login_hint: trimmedEmail
                  } : {}
                });
              } catch (error) {
                console.error('Login error:', error);
              }
            }}
            style={{
              backgroundColor: '#38b44a',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2d8f3a'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#38b44a'}
          >
            Log In
          </button>
          <button 
            onClick={handleSignup}
            disabled={isSavingEmail}
            style={{
              backgroundColor: 'transparent',
              color: isSavingEmail ? '#999' : '#38b44a',
              border: `2px solid ${isSavingEmail ? '#999' : '#38b44a'}`,
              padding: '10px 22px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isSavingEmail ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: isSavingEmail ? 0.7 : 1
            }}
            onMouseOver={(e) => {
              if (!isSavingEmail) {
                e.target.style.backgroundColor = '#38b44a';
                e.target.style.color = 'white';
              }
            }}
            onMouseOut={(e) => {
              if (!isSavingEmail) {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#38b44a';
              }
            }}
          >
            {isSavingEmail ? 'Saving...' : 'Sign Up'}
          </button>
        </div>
        
        {/* Forgot Password Link */}
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button
            onClick={async () => {
              if (!email) {
                showMessage('Please enter your email address first to reset your password.', 'error');
                return;
              }
              
              try {
                // Call backend to send password reset email
                const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/auth/reset-password`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ email: email }),
                });
                
                if (response.ok) {
                  showMessage('Password reset email sent! Please check your inbox.', 'success');
                } else {
                  const errorData = await response.json();
                  showMessage(errorData.message || 'Failed to send reset email. Please try again.', 'error');
                }
              } catch (error) {
                console.error('Password reset error:', error);
                showMessage('Failed to send reset email. Please try again.', 'error');
              }
            }}
            style={{
              backgroundColor: 'transparent',
              color: '#38b44a',
              border: 'none',
              padding: '8px 16px',
              fontSize: '14px',
              textDecoration: 'underline',
              cursor: 'pointer',
              transition: 'color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.color = '#2d8f3a'}
            onMouseOut={(e) => e.target.style.color = '#38b44a'}
          >
            Forgot your password?
          </button>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <CSRFProvider>
        <Toaster 
          position="top-center"
          duration={5000}
          containerStyle={{
            top: 100,
          }}
          toastOptions={{
            duration: 5000,
            style: {
              background: '#333',
              color: '#fff',
              fontSize: '16px',
              padding: '20px 25px',
              fontWeight: '500',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              minWidth: '300px',
            },
            success: {
              iconTheme: {
                primary: '#38b44a',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ff4d4f',
                secondary: '#fff',
              },
            },
          }}
        />
        <BackgroundAccents />
        <NavBar />
        <ScrollToTop />
        <MemberActivityReporter />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/sign-up" element={<SignUp />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/account" element={<Account />} />
        <Route path="/plans" element={<Plans />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/opportunities" element={<Opportunities />} />
        <Route path="/opportunity/:hash" element={<OpportunityDetail />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/podcast" element={<Podcast />} />
        <Route path="/forum" element={<Forum />} />
        <Route path="/classifieds" element={<Classifieds />} />
        <Route path="/forum/:id" element={<ThreadDetail />} />
        <Route path="/profile/:id" element={<MemberProfile />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/plug-your-work" element={<PlugYourWork />} />
        <Route path="/plug-your-work/submit" element={<PlugYourWorkSubmit />} />
            <Route path="/editor-database" element={<EditorDatabase />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
      </Routes>
      <Footer />
      </CSRFProvider>
    </QueryClientProvider>
  );
}

export default App;